import { useState } from "react";
import { MapPin, Calendar, Images, Maximize2 } from "lucide-react";
import ProjectDialog from "@/components/ProjectDialog";

export default function ProjectCard({ project, index = 0, span }) {
    const [open, setOpen] = useState(false);
    const imageCount = project.images?.length || 1;
    const cover = project.image || project.images?.[0]?.url;

    return (
        <>
            <article
                data-testid={`project-card-${project.id}`}
                className={`card-ns overflow-hidden group cursor-pointer flex flex-col ${span || ""}`}
                onClick={() => setOpen(true)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Åbn projekt: ${project.title}`}
            >
                <div className="relative overflow-hidden aspect-[4/3]">
                    <img
                        src={cover}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/95 backdrop-blur text-[var(--ns-blue)] text-[11px] font-bold tracking-widest uppercase">
                            {project.categoryLabel}
                        </span>
                        {project.status === "in-progress" && (
                            <span
                                data-testid={`project-status-${project.id}`}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--ns-blue)] text-white text-[11px] font-bold tracking-widest uppercase"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                Igangværende
                            </span>
                        )}
                        {imageCount > 1 && (
                            <span
                                data-testid={`project-image-count-${project.id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white text-[11px] font-bold"
                            >
                                <Images className="w-3 h-3" />
                                {imageCount}
                            </span>
                        )}
                    </div>
                    <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white text-[var(--ns-blue)] grid place-items-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Maximize2 className="w-4 h-4" />
                    </div>
                </div>
                <div className="p-6 sm:p-7 flex flex-col flex-1">
                    <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight text-[var(--ns-text)] mb-2">
                        {project.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--ns-muted)] mb-4 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" /> {project.location}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" /> {project.year}
                        </span>
                    </div>
                    <p className="text-[var(--ns-muted)] text-sm leading-relaxed mb-4 flex-1">
                        {project.description}
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-[var(--ns-blue)] font-display group-hover:gap-3 transition-all">
                        Se projektet
                        <Maximize2 className="w-3.5 h-3.5" />
                    </span>
                </div>
            </article>
            <ProjectDialog
                project={project}
                open={open}
                onOpenChange={setOpen}
            />
        </>
    );
}
